/*
 * collection-banner-layout.js
 * ---------------------------------------------------------------------------
 * Orphan-free placement of metaobject-driven collection grid banners, designed
 * to work WITH the theme's native pagination AND "Auto load products on scroll"
 * (infinite scroll). Banners come from the first-page <template> (hidden on
 * filtered views). Variant cards are produced by the section Liquid and are
 * treated as ordinary product cards here.
 *
 * POSITION MODEL: each banner's position is a 1-based CELL SLOT in the combined
 * grid (counting left-to-right, top-to-bottom), so it encodes both row and
 * column - a 50% banner can sit at the left, middle or right of a row. Span =
 * clamp(round(width/100 * liveColumns), 1, cols). If a banner can't fit the
 * remaining cells of its row, products fill the row and it bumps to the next -
 * no orphan gaps.
 *
 * Banners are inserted AMONG the existing product cards without reordering the
 * products themselves, so infinite scroll (which appends product cards) and the
 * PaginatedList component keep working. When more cards auto-load, banners are
 * re-placed (they stay near the top / first page).
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var DESKTOP_MQ = '(min-width: 750px)';
  function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
  function isBanner(el) { return el.hasAttribute && el.hasAttribute('data-collection-banner'); }

  function parseColumns(grid) {
    var tpl = '';
    try { tpl = getComputedStyle(grid).gridTemplateColumns; } catch (e) {}
    if (tpl && tpl !== 'none') {
      var n = tpl.trim().split(/\s+/).filter(Boolean).length;
      if (n > 0) return n;
    }
    if (window.matchMedia(DESKTOP_MQ).matches) return 4;
    return grid.classList.contains('product-grid-mobile--large') ? 1 : 2;
  }

  // Cell-slot placement. items/banners are arrays; banners: {el,pos,span}.
  // Returns ordered [{el, span, rowbreak}] interleaving products + banners.
  function placeGroup(items, banners, columns) {
    banners = banners.slice().sort(function (a, b) { return a.pos - b.pos; });
    var out = [], pi = 0, bi = 0, slot = 0, col = 0;
    while (pi < items.length || bi < banners.length) {
      var placed = false;
      if (bi < banners.length) {
        var b = banners[bi];
        var target = b.pos - 1;
        var span = clamp(b.span, 1, columns);
        var due = slot >= target || pi >= items.length;
        if (due) {
          if (col + span <= columns) {
            out.push({ el: b.el, span: span }); slot += span; col = (col + span) % columns; bi++; placed = true;
          } else if (pi < items.length) {
            out.push({ el: items[pi], span: 1 }); slot++; col = (col + 1) % columns; pi++; placed = true;
          } else {
            slot += columns - col; col = 0;
            out.push({ el: b.el, span: span, rowbreak: true }); slot += span; col = (col + span) % columns; bi++; placed = true;
          }
        }
      }
      if (!placed) {
        if (pi < items.length) { out.push({ el: items[pi], span: 1 }); slot++; col = (col + 1) % columns; pi++; }
        else break;
      }
    }
    return out;
  }

  function BannerGrid(grid) {
    this.grid = grid;
    this.banners = [];
    this.processed = [];
    this._guard = false;
    this._raf = null;
    this.collect();
    this.layout();
    this.bind();
  }

  BannerGrid.prototype.collect = function () {
    var self = this;
    var tpls = this.grid.querySelectorAll('template[data-collection-banners]');
    Array.prototype.forEach.call(tpls, function (tpl) {
      if (self.processed.indexOf(tpl) !== -1) return;
      self.processed.push(tpl);
      var nodes = tpl.content.querySelectorAll('[data-collection-banner]');
      Array.prototype.forEach.call(nodes, function (node) {
        var el = node.cloneNode(true);
        var pd = parseInt(el.getAttribute('data-cb-desktop-pos'), 10);
        var pm = parseInt(el.getAttribute('data-cb-mobile-pos'), 10);
        self.banners.push({
          el: el,
          posD: isNaN(pd) ? null : pd,
          posM: isNaN(pm) ? null : pm,
          wD: parseFloat(el.getAttribute('data-cb-desktop-width')) || 100,
          wM: parseFloat(el.getAttribute('data-cb-mobile-width')) || 100
        });
      });
    });
  };

  BannerGrid.prototype.products = function () {
    return Array.prototype.slice.call(this.grid.querySelectorAll(':scope > li.product-grid__item'));
  };

  BannerGrid.prototype.layout = function () {
    var grid = this.grid;
    if (!grid.isConnected) { this.teardown(); return; }
    var columns = parseColumns(grid);
    var isDesktop = window.matchMedia(DESKTOP_MQ).matches;
    var products = this.products();

    var bs = this.banners
      .map(function (b) { return { el: b.el, pos: isDesktop ? b.posD : b.posM, span: clamp(Math.round((isDesktop ? b.wD : b.wM) / 100 * columns), 1, columns) }; })
      .filter(function (b) { return b.pos != null; });

    this._guard = true;

    if (products.length === 0 || bs.length === 0) {
      // nothing to place - make sure no banner clones linger
      this.banners.forEach(function (b) { if (b.el.parentNode) b.el.parentNode.removeChild(b.el); });
      var s0 = this; requestAnimationFrame(function () { s0._guard = false; });
      return;
    }

    var ordered = placeGroup(products, bs, columns);

    // Insert ONLY the banners among the products (never move product cards),
    // so infinite scroll + PaginatedList are undisturbed.
    for (var i = 0; i < ordered.length; i++) {
      var it = ordered[i];
      if (!isBanner(it.el)) continue;
      var anchor = null;
      for (var j = i + 1; j < ordered.length; j++) {
        if (!isBanner(ordered[j].el)) { anchor = ordered[j].el; break; }
      }
      it.el.style.display = '';
      it.el.style.gridColumn = it.rowbreak ? '1 / span ' + it.span : (it.span >= columns ? '1 / -1' : 'span ' + it.span);
      if (anchor) grid.insertBefore(it.el, anchor);
      else grid.appendChild(it.el);
    }

    var self = this;
    requestAnimationFrame(function () { self._guard = false; });
  };

  BannerGrid.prototype.relayout = function () {
    var self = this;
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(function () { self._raf = null; self.layout(); });
  };

  // Re-read banners from the (possibly morphed) template and re-place.
  BannerGrid.prototype.reset = function () {
    if (!this.grid.isConnected) { this.teardown(); return; }
    this.banners.forEach(function (b) { if (b.el.parentNode) b.el.parentNode.removeChild(b.el); });
    Array.prototype.forEach.call(this.grid.querySelectorAll(':scope > [data-collection-banner]'), function (n) {
      if (n.parentNode) n.parentNode.removeChild(n);
    });
    this.banners = [];
    this.processed = [];
    this.collect();
    this.layout();
  };

  BannerGrid.prototype.bind = function () {
    var self = this;
    if (window.ResizeObserver) { this.ro = new ResizeObserver(function () { self.relayout(); }); this.ro.observe(this.grid); }
    else window.addEventListener('resize', function () { self.relayout(); });

    this.mo = new MutationObserver(function (muts) {
      if (self._guard) return;
      var relevant = muts.some(function (m) {
        if (m.type === 'attributes' && m.attributeName === 'product-grid-view') return true;
        if (m.type === 'childList') {
          var nodes = [].slice.call(m.addedNodes).concat([].slice.call(m.removedNodes));
          return nodes.some(function (n) { return n.nodeType === 1 && n.classList && n.classList.contains('product-grid__item'); });
        }
        return false;
      });
      if (relevant) self.relayout();
    });
    this.mo.observe(this.grid, { childList: true, attributes: true, attributeFilter: ['product-grid-view'] });
  };

  BannerGrid.prototype.teardown = function () {
    if (this.ro) { try { this.ro.disconnect(); } catch (e) {} }
    if (this.mo) { try { this.mo.disconnect(); } catch (e) {} }
  };

  function init() {
    var grids = document.querySelectorAll('results-list[data-collection-grid] .product-grid');
    Array.prototype.forEach.call(grids, function (grid) {
      if (grid.__cbGrid) return;
      grid.__cbGrid = new BannerGrid(grid);
    });
  }

  // After a sort/filter morph settles, re-place banners (template may have been
  // removed when filtered, or re-added when cleared).
  var _mo = null, _t = null, _safety = null;
  function settleThenReconcile() {
    if (_mo) _mo.disconnect();
    if (_t) clearTimeout(_t);
    if (_safety) clearTimeout(_safety);
    var done = function () {
      if (_mo) { _mo.disconnect(); _mo = null; }
      if (_t) { clearTimeout(_t); _t = null; }
      if (_safety) { clearTimeout(_safety); _safety = null; }
      reconcile();
    };
    _mo = new MutationObserver(function () { if (_t) clearTimeout(_t); _t = setTimeout(done, 150); });
    Array.prototype.forEach.call(document.querySelectorAll('[id^="shopify-section-"]'), function (s) {
      _mo.observe(s, { childList: true, subtree: true, attributes: true });
    });
    _safety = setTimeout(done, 1500);
  }
  function reconcile() {
    var grids = document.querySelectorAll('results-list[data-collection-grid] .product-grid');
    Array.prototype.forEach.call(grids, function (grid) {
      if (grid.__cbGrid && grid.isConnected) grid.__cbGrid.reset();
      else if (grid.isConnected) grid.__cbGrid = new BannerGrid(grid);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('filter:update', settleThenReconcile);
})();
